import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { CheckIcon, ChevronDownIcon } from "@chakra-ui/icons";
import axios from "axios";
import { collection, getDocs } from "firebase/firestore";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { db } from "../../configs/firebase";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

type FirestoreWarehouse = {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  isActive?: boolean;
};

type FirestoreBusinessUser = {
  id: string;
  name?: string;
  email?: string;
  role?: string;
  warehouses?: FirestoreWarehouse[];
};

type DriverAvailability = {
  online: boolean;
  eligible: boolean;
  reason: string;
  currentJobs: number;
  maxConcurrentJobs: number;
};

type MenuChoice = {
  value: string;
  label: string;
  description?: string;
  searchText?: string;
  disabled?: boolean;
  rightContent?: React.ReactNode;
};

type SearchableMenuProps = {
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  value: string;
  options: MenuChoice[];
  onChange: (value: string) => void;
  theme: any;
  isDisabled?: boolean;
  emptyText?: string;
};

const toArray = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.drivers)) return payload.drivers;
  if (Array.isArray(payload?.assignments)) return payload.assignments;
  if (Array.isArray(payload?.data?.drivers)) return payload.data.drivers;
  if (Array.isArray(payload?.data?.assignments))
    return payload.data.assignments;
  return [];
};

const getDriverId = (driver: any) =>
  String(
    driver?.driverId ||
      driver?.id ||
      driver?._id ||
      driver?.userId ||
      driver?.firebaseUid ||
      driver?.authUid ||
      "",
  ).trim();

const getDriverPhone = (driver: any) =>
  String(
    driver?.whatsappNumber || driver?.phoneNumber || driver?.phone || "",
  ).trim();

const getBusinessLabel = (user?: FirestoreBusinessUser | null) =>
  String(user?.name || user?.email || user?.id || "").trim();

const getDriverAvailability = (driver: any): DriverAvailability => {
  const status = String(driver?.status || "").toLowerCase();
  const shiftStatus = String(driver?.currentShiftStatus || "").toLowerCase();
  const currentJobs = Math.max(0, Number(driver?.currentJobsCount || 0));
  const maxConcurrentJobs = Math.max(1, Number(driver?.maxConcurrentJobs || 2));

  const serviceCategories = Array.isArray(driver?.serviceCategories)
    ? driver.serviceCategories.map((value: any) =>
        String(value).trim().toLowerCase(),
      )
    : [];

  const online = status === "active";
  const onShift = shiftStatus === "on_shift";
  const hasCapacity = currentJobs < maxConcurrentJobs;
  const supportsDispatch =
    serviceCategories.length === 0 ||
    serviceCategories.includes("delivery") ||
    serviceCategories.includes("product_order");

  const eligible = online && onShift && hasCapacity && supportsDispatch;

  let reason = "Ready for jobs";

  if (!online) {
    reason = "Driver is offline";
  } else if (!onShift) {
    reason = "Driver is off shift";
  } else if (!hasCapacity) {
    reason = `At capacity (${currentJobs}/${maxConcurrentJobs})`;
  } else if (!supportsDispatch) {
    reason = "Delivery service not enabled";
  }

  return {
    online,
    eligible,
    reason,
    currentJobs,
    maxConcurrentJobs,
  };
};

const normalizeAssignedDrivers = (payload: any, allDrivers: any[]) => {
  const rows = toArray(payload);

  return rows
    .map((row: any) => {
      const assignment = row?.assignment || row || {};
      const embeddedDriver = row?.driver || null;

      const driverId = String(
        assignment?.driverId || embeddedDriver?.id || embeddedDriver?._id || "",
      ).trim();

      if (!driverId) return null;

      const fallbackDriver =
        allDrivers.find((driver) => getDriverId(driver) === driverId) || null;

      const driver = embeddedDriver || fallbackDriver;

      return {
        ...(driver || {}),
        id: getDriverId(driver) || driverId,
        driverId,
        name: driver?.name || assignment?.driverName || `Driver ${driverId}`,
        phoneNumber: driver?.phoneNumber || assignment?.driverPhone || "",
        whatsappNumber: driver?.whatsappNumber || assignment?.driverPhone || "",
        assignmentId: assignment?.id || row?.id || "",
        ownerUserId: assignment?.ownerUserId || "",
        warehouseId: assignment?.warehouseId || "",
        isPrimary: assignment?.isPrimary === true,
        isBackup: assignment?.isBackup === true,
        orderTypes: Array.isArray(assignment?.orderTypes)
          ? assignment.orderTypes
          : [],
        active: assignment?.active !== false,
        driverResolved: row?.driverResolved ?? Boolean(driver),
        assignment,
      };
    })
    .filter(Boolean) as any[];
};

const SearchableMenu: React.FC<SearchableMenuProps> = ({
  label,
  placeholder,
  searchPlaceholder,
  value,
  options,
  onChange,
  theme,
  isDisabled = false,
  emptyText = "No options found.",
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [search, setSearch] = useState("");

  const selected = options.find((option) => option.value === value) || null;

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return options;

    return options.filter((option) => {
      const searchable = [option.label, option.description, option.searchText]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [options, search]);

  const closeMenu = () => {
    setSearch("");
    onClose();
  };

  return (
    <Box minW={0}>
      <Text mb={2} fontSize="sm" fontWeight="700" color={theme.text}>
        {label}
      </Text>

      <Menu
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={closeMenu}
        closeOnSelect={false}
        placement="bottom-start"
      >
        <MenuButton
          as={Button}
          w="100%"
          h="44px"
          px={3}
          rightIcon={<ChevronDownIcon />}
          isDisabled={isDisabled}
          border={`1px solid ${theme.inputBorder || theme.border}`}
          borderRadius="10px"
          bg={theme.inputBg || theme.surface}
          color={theme.text}
          fontSize="sm"
          fontWeight="600"
          textAlign="left"
          _hover={{
            borderColor: theme.inputBorderFocus || theme.primary,
            bg: theme.inputBg || theme.surface,
          }}
          _active={{ bg: theme.inputBg || theme.surface }}
          _focus={{
            borderColor: theme.inputBorderFocus || theme.primary,
            boxShadow: `0 0 0 1px ${
              theme.inputBorderFocus || theme.primary || "#3182ce"
            }`,
          }}
        >
          <Text
            noOfLines={1}
            color={
              selected ? theme.text : theme.inputPlaceholder || theme.textMuted
            }
          >
            {selected?.label || placeholder}
          </Text>
        </MenuButton>

        <MenuList
          p={0}
          minW={{ base: "280px", md: "360px" }}
          maxW="min(92vw, 520px)"
          maxH="360px"
          overflowY="auto"
          borderRadius="12px"
          border={`1px solid ${theme.border}`}
          bg={theme.surface}
          boxShadow={theme.shadowMd || theme.shadowSm}
          zIndex={50}
        >
          <Box
            position="sticky"
            top={0}
            zIndex={2}
            p={2}
            bg={theme.surface}
            borderBottom={`1px solid ${theme.border}`}
          >
            <Input
              size="sm"
              h="38px"
              value={search}
              placeholder={searchPlaceholder}
              borderRadius="8px"
              bg={theme.inputBg || theme.surface}
              borderColor={theme.inputBorder || theme.border}
              color={theme.text}
              _placeholder={{
                color: theme.inputPlaceholder || theme.textMuted,
              }}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
              onChange={(event) => setSearch(event.target.value)}
              autoFocus
            />
          </Box>

          <Box p={1}>
            {filteredOptions.length === 0 ? (
              <Text p={3} fontSize="sm" color={theme.textMuted}>
                {emptyText}
              </Text>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = option.value === value;

                return (
                  <MenuItem
                    key={option.value}
                    isDisabled={option.disabled}
                    px={3}
                    py={2.5}
                    mb={1}
                    borderRadius="8px"
                    bg={
                      isSelected
                        ? theme.cardBg || theme.surfaceAlt || theme.background
                        : "transparent"
                    }
                    _hover={{
                      bg: theme.hover || theme.surfaceAlt || theme.background,
                    }}
                    _focus={{
                      bg: theme.hover || theme.surfaceAlt || theme.background,
                    }}
                    onClick={() => {
                      if (option.disabled) return;
                      onChange(option.value);
                      closeMenu();
                    }}
                  >
                    <HStack
                      w="100%"
                      justify="space-between"
                      align="center"
                      spacing={3}
                    >
                      <Box minW={0} flex={1}>
                        <HStack spacing={2}>
                          <Text
                            fontSize="sm"
                            fontWeight={isSelected ? "800" : "700"}
                            color={theme.text}
                            noOfLines={1}
                          >
                            {option.label}
                          </Text>

                          {isSelected && (
                            <CheckIcon
                              boxSize={3}
                              color={theme.primary || "green.500"}
                            />
                          )}
                        </HStack>

                        {option.description && (
                          <Text
                            mt={0.5}
                            fontSize="xs"
                            color={theme.textMuted}
                            noOfLines={2}
                          >
                            {option.description}
                          </Text>
                        )}
                      </Box>

                      {option.rightContent && (
                        <Box flexShrink={0}>{option.rightContent}</Box>
                      )}
                    </HStack>
                  </MenuItem>
                );
              })
            )}
          </Box>
        </MenuList>
      </Menu>
    </Box>
  );
};

const WarehouseAssignmentsPage: React.FC = () => {
  const { theme } = useCustomTheme();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [assignmentLoadError, setAssignmentLoadError] = useState("");

  const [drivers, setDrivers] = useState<any[]>([]);
  const [businessUsers, setBusinessUsers] = useState<FirestoreBusinessUser[]>(
    [],
  );
  const [warehouseAssignmentRows, setWarehouseAssignmentRows] = useState<any[]>(
    [],
  );

  const [selectedOwnerUserId, setSelectedOwnerUserId] = useState("");
  const [selectedWarehouseId, setSelectedWarehouseId] = useState("");
  const [selectedDriverId, setSelectedDriverId] = useState("");

  const selectedBusiness = useMemo(
    () => businessUsers.find((user) => user.id === selectedOwnerUserId) || null,
    [businessUsers, selectedOwnerUserId],
  );

  const availableWarehouses = useMemo(() => {
    if (!Array.isArray(selectedBusiness?.warehouses)) return [];

    return (selectedBusiness?.warehouses || []).filter(
      (warehouse) => warehouse.isActive !== false,
    );
  }, [selectedBusiness]);

  const selectedWarehouse = useMemo(
    () =>
      availableWarehouses.find(
        (warehouse) => warehouse.id === selectedWarehouseId,
      ) || null,
    [availableWarehouses, selectedWarehouseId],
  );

  const selectedDriver = useMemo(
    () =>
      drivers.find((driver) => getDriverId(driver) === selectedDriverId) ||
      null,
    [drivers, selectedDriverId],
  );

  const assignedDrivers = useMemo(
    () => normalizeAssignedDrivers(warehouseAssignmentRows, drivers),
    [warehouseAssignmentRows, drivers],
  );

  const assignedDriverIds = useMemo(
    () =>
      new Set(
        assignedDrivers.map((driver) => getDriverId(driver)).filter(Boolean),
      ),
    [assignedDrivers],
  );

  const driverStats = useMemo(() => {
    const online = drivers.filter(
      (driver) => getDriverAvailability(driver).online,
    ).length;

    const eligible = drivers.filter(
      (driver) => getDriverAvailability(driver).eligible,
    ).length;

    return {
      total: drivers.length,
      online,
      eligible,
    };
  }, [drivers]);

  const loadDrivers = async () => {
    const response = await axios.get(`${API_BASE}/api/v1/drivers`);
    return response.data?.data || [];
  };

  const loadBusinessUsers = async () => {
    const snapshot = await getDocs(collection(db, "users"));
    const users: FirestoreBusinessUser[] = [];

    snapshot.forEach((docSnap) => {
      const data: any = docSnap.data();
      const warehouses = Array.isArray(data.warehouses) ? data.warehouses : [];

      if (
        warehouses.length > 0 ||
        String(data.role || "").toLowerCase() === "shop"
      ) {
        users.push({
          id: docSnap.id,
          name: data.name || data.displayName || data.businessName || "",
          email: data.email || "",
          role: data.role || "",
          warehouses,
        });
      }
    });

    return users.sort((left, right) =>
      getBusinessLabel(left).localeCompare(getBusinessLabel(right)),
    );
  };

  const loadWarehouseAssignments = useCallback(
    async (warehouseId: string, ownerUserId: string) => {
      if (!warehouseId || !ownerUserId) {
        setWarehouseAssignmentRows([]);
        setAssignmentLoadError("");
        return;
      }

      try {
        setLoadingAssignments(true);
        setAssignmentLoadError("");

        const response = await axios.get(
          `${API_BASE}/api/v1/warehouses/${encodeURIComponent(
            warehouseId,
          )}/drivers`,
          {
            params: {
              ownerUserId,
            },
          },
        );

        setWarehouseAssignmentRows(
          Array.isArray(response.data?.data) ? response.data.data : [],
        );
      } catch (error: any) {
        console.error("Failed to load warehouse drivers:", error);

        setWarehouseAssignmentRows([]);
        setAssignmentLoadError(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Could not load drivers assigned to this warehouse.",
        );
      } finally {
        setLoadingAssignments(false);
      }
    },
    [],
  );

  const load = async () => {
    try {
      setLoading(true);

      const [driverRows, userRows] = await Promise.all([
        loadDrivers(),
        loadBusinessUsers(),
      ]);

      setDrivers(driverRows);
      setBusinessUsers(userRows);
    } catch (error) {
      console.error("Failed to load assignment page data:", error);

      toast({
        title: "Failed to load data",
        description: "Could not load drivers or warehouse information.",
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setSelectedWarehouseId("");
    setSelectedDriverId("");
    setWarehouseAssignmentRows([]);
    setAssignmentLoadError("");
  }, [selectedOwnerUserId]);

  useEffect(() => {
    setSelectedDriverId("");

    if (selectedWarehouseId && selectedOwnerUserId) {
      loadWarehouseAssignments(selectedWarehouseId, selectedOwnerUserId);
    } else {
      setWarehouseAssignmentRows([]);
      setAssignmentLoadError("");
    }
  }, [selectedWarehouseId, selectedOwnerUserId, loadWarehouseAssignments]);

  const businessOptions = useMemo<MenuChoice[]>(
    () =>
      businessUsers.map((user) => {
        const warehouseCount = (user.warehouses || []).filter(
          (warehouse) => warehouse.isActive !== false,
        ).length;

        return {
          value: user.id,
          label: getBusinessLabel(user),
          description:
            user.email && user.name
              ? user.email
              : user.role
                ? `Role: ${user.role}`
                : "",
          searchText: [user.id, user.name, user.email, user.role]
            .filter(Boolean)
            .join(" "),
          rightContent: (
            <Badge colorScheme="blue" variant="subtle" borderRadius="6px">
              {warehouseCount}{" "}
              {warehouseCount === 1 ? "warehouse" : "warehouses"}
            </Badge>
          ),
        };
      }),
    [businessUsers],
  );

  const warehouseOptions = useMemo<MenuChoice[]>(
    () =>
      availableWarehouses.map((warehouse) => ({
        value: warehouse.id,
        label: warehouse.name,
        description: warehouse.address,
        searchText: [warehouse.name, warehouse.address, warehouse.id]
          .filter(Boolean)
          .join(" "),
        rightContent: (
          <Badge colorScheme="green" variant="subtle" borderRadius="6px">
            Active
          </Badge>
        ),
      })),
    [availableWarehouses],
  );

  const driverOptions = useMemo<MenuChoice[]>(() => {
    return [...drivers]
      .sort((left, right) => {
        const leftAvailability = getDriverAvailability(left);
        const rightAvailability = getDriverAvailability(right);

        if (leftAvailability.eligible !== rightAvailability.eligible) {
          return leftAvailability.eligible ? -1 : 1;
        }

        if (leftAvailability.online !== rightAvailability.online) {
          return leftAvailability.online ? -1 : 1;
        }

        return String(left?.name || "").localeCompare(
          String(right?.name || ""),
        );
      })
      .map((driver) => {
        const id = getDriverId(driver);
        const availability = getDriverAvailability(driver);
        const alreadyAssigned = Boolean(id && assignedDriverIds.has(id));

        return {
          value: id,
          label: driver?.name || "Unnamed driver",
          description: [
            getDriverPhone(driver) || "No phone",
            driver?.vehicleType || "No vehicle",
            `${availability.currentJobs}/${availability.maxConcurrentJobs} jobs`,
          ].join(" • "),
          searchText: [
            id,
            driver?.name,
            driver?.phoneNumber,
            driver?.whatsappNumber,
            driver?.vehicleType,
          ]
            .filter(Boolean)
            .join(" "),
          disabled: !id || alreadyAssigned,
          rightContent: alreadyAssigned ? (
            <Badge colorScheme="blue" borderRadius="6px">
              Assigned
            </Badge>
          ) : (
            <VStack spacing={1} align="flex-end">
              <Badge
                colorScheme={availability.online ? "green" : "gray"}
                borderRadius="6px"
              >
                {availability.online ? "Online" : "Offline"}
              </Badge>
              <Badge
                colorScheme={availability.eligible ? "green" : "orange"}
                borderRadius="6px"
                variant="subtle"
              >
                {availability.eligible ? "Eligible" : "Not eligible"}
              </Badge>
            </VStack>
          ),
        };
      })
      .filter((option) => Boolean(option.value));
  }, [drivers, assignedDriverIds]);

  const assign = async () => {
    if (!selectedOwnerUserId) {
      toast({ title: "Select a business first", status: "warning" });
      return;
    }

    if (!selectedWarehouseId || !selectedWarehouse) {
      toast({ title: "Select a warehouse first", status: "warning" });
      return;
    }

    if (!selectedDriverId || !selectedDriver) {
      toast({ title: "Select a driver first", status: "warning" });
      return;
    }

    const actualDriverId = getDriverId(selectedDriver);

    if (!actualDriverId) {
      toast({
        title: "Unable to assign driver",
        description: "The selected driver does not have a valid driver ID.",
        status: "error",
      });
      return;
    }

    if (assignedDriverIds.has(actualDriverId)) {
      toast({
        title: "Driver already assigned",
        description: `${selectedDriver.name || "This driver"} is already attached to ${selectedWarehouse.name}.`,
        status: "info",
      });
      return;
    }

    try {
      setAssigning(true);

      await axios.post(
        `${API_BASE}/api/v1/warehouses/${encodeURIComponent(
          selectedWarehouseId,
        )}/drivers/${encodeURIComponent(actualDriverId)}`,
        {
          ownerUserId: selectedOwnerUserId,
          isPrimary: true,
          isBackup: false,
          orderTypes: ["delivery", "product_order"],
        },
      );

      toast({
        title: "Driver assigned",
        description: `${selectedDriver.name || "Driver"} is now assigned to ${selectedWarehouse.name}.`,
        status: "success",
      });

      setSelectedDriverId("");

      const refreshedDrivers = await loadDrivers();
      setDrivers(refreshedDrivers);

      await loadWarehouseAssignments(selectedWarehouseId, selectedOwnerUserId);
    } catch (error: any) {
      console.error("Failed to assign warehouse driver:", error);

      toast({
        title: "Failed to assign driver",
        description:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Please try again.",
        status: "error",
      });
    } finally {
      setAssigning(false);
    }
  };

  const clearSelection = () => {
    setSelectedOwnerUserId("");
    setSelectedWarehouseId("");
    setSelectedDriverId("");
    setWarehouseAssignmentRows([]);
    setAssignmentLoadError("");
  };

  const canAssign = Boolean(
    selectedOwnerUserId &&
    selectedWarehouseId &&
    selectedDriverId &&
    selectedWarehouse &&
    selectedDriver,
  );

  return (
    <Box p={{ base: 0, md: 2 }} bg={theme.background} minH="100%" minW={0}>
      <Box mb={5}>
        <Text
          fontSize={{ base: "xl", md: "2xl" }}
          fontWeight="800"
          color={theme.text}
        >
          Warehouse Drivers
        </Text>
        <Text mt={1} fontSize="sm" color={theme.textMuted}>
          Assign drivers to warehouses and see exactly who can receive jobs.
        </Text>
      </Box>

      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3} mb={5}>
        <Box
          p={4}
          borderRadius="10px"
          border={`1px solid ${theme.border}`}
          bg={theme.surface}
        >
          <Text fontSize="xs" color={theme.textMuted}>
            Total drivers
          </Text>
          <Text mt={1} fontSize="xl" fontWeight="800" color={theme.text}>
            {driverStats.total}
          </Text>
        </Box>

        <Box
          p={4}
          borderRadius="10px"
          border={`1px solid ${theme.border}`}
          bg={theme.surface}
        >
          <Text fontSize="xs" color={theme.textMuted}>
            Online
          </Text>
          <Text mt={1} fontSize="xl" fontWeight="800" color={theme.text}>
            {driverStats.online}
          </Text>
        </Box>

        <Box
          p={4}
          borderRadius="10px"
          border={`1px solid ${theme.border}`}
          bg={theme.surface}
        >
          <Text fontSize="xs" color={theme.textMuted}>
            Eligible now
          </Text>
          <Text mt={1} fontSize="xl" fontWeight="800" color={theme.text}>
            {driverStats.eligible}
          </Text>
        </Box>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5} alignItems="start">
        <Box
          p={{ base: 3, md: 5 }}
          borderRadius="12px"
          bg={theme.surface}
          border={`1px solid ${theme.border}`}
          boxShadow={theme.shadowSm}
        >
          <Box mb={5}>
            <Text fontWeight="800" fontSize="lg" color={theme.text}>
              Assign Driver
            </Text>
            <Text mt={1} fontSize="sm" color={theme.textMuted}>
              Choose a business, warehouse and driver.
            </Text>
          </Box>

          <VStack spacing={4} align="stretch">
            <SearchableMenu
              label="Business"
              placeholder="Select business"
              searchPlaceholder="Search businesses..."
              value={selectedOwnerUserId}
              options={businessOptions}
              onChange={setSelectedOwnerUserId}
              theme={theme}
              emptyText="No businesses found."
            />

            <SearchableMenu
              label="Warehouse"
              placeholder={
                selectedBusiness
                  ? "Select warehouse"
                  : "Select a business first"
              }
              searchPlaceholder="Search warehouses..."
              value={selectedWarehouseId}
              options={warehouseOptions}
              onChange={setSelectedWarehouseId}
              theme={theme}
              isDisabled={!selectedBusiness}
              emptyText="No active warehouses found."
            />

            <SearchableMenu
              label="Driver"
              placeholder={
                selectedWarehouse
                  ? "Select driver to add"
                  : "Select a warehouse first"
              }
              searchPlaceholder="Search by name, phone or vehicle..."
              value={selectedDriverId}
              options={driverOptions}
              onChange={setSelectedDriverId}
              theme={theme}
              isDisabled={!selectedWarehouse}
              emptyText="No matching drivers found."
            />

            {selectedDriver && (
              <Box
                p={3}
                borderRadius="9px"
                bg={theme.surfaceAlt}
                border={`1px solid ${theme.border}`}
              >
                {(() => {
                  const availability = getDriverAvailability(selectedDriver);

                  return (
                    <HStack justify="space-between" align="start" spacing={3}>
                      <Box minW={0}>
                        <Text fontWeight="700" color={theme.text} noOfLines={1}>
                          {selectedDriver.name || "Unnamed driver"}
                        </Text>
                        <Text mt={0.5} fontSize="xs" color={theme.textMuted}>
                          {getDriverPhone(selectedDriver) || "No phone"} •{" "}
                          {selectedDriver.vehicleType || "No vehicle"}
                        </Text>
                        {!availability.eligible && (
                          <Text mt={1} fontSize="xs" color={theme.textMuted}>
                            {availability.reason}
                          </Text>
                        )}
                      </Box>

                      <VStack spacing={1} align="flex-end" flexShrink={0}>
                        <Badge
                          colorScheme={availability.online ? "green" : "gray"}
                          borderRadius="6px"
                        >
                          {availability.online ? "Online" : "Offline"}
                        </Badge>
                        <Badge
                          colorScheme={
                            availability.eligible ? "green" : "orange"
                          }
                          borderRadius="6px"
                        >
                          {availability.eligible ? "Eligible" : "Not eligible"}
                        </Badge>
                      </VStack>
                    </HStack>
                  );
                })()}
              </Box>
            )}

            <Divider />

            <HStack
              spacing={3}
              flexWrap="wrap"
              align="stretch"
              sx={{
                "& > button": {
                  flex: { base: "1 1 100%", sm: "0 0 auto" },
                },
              }}
            >
              <Button
                onClick={assign}
                isDisabled={!canAssign}
                isLoading={assigning}
                loadingText="Assigning"
                color={theme.textOnPrimary}
                bgImage={theme.gradients.primaryButton}
                _hover={{ bgImage: theme.gradients.primaryButtonHover }}
              >
                Assign Driver
              </Button>

              <Button
                variant="outline"
                onClick={clearSelection}
                isDisabled={assigning}
              >
                Clear
              </Button>

              <Button variant="ghost" onClick={load} isDisabled={assigning}>
                Refresh
              </Button>
            </HStack>
          </VStack>
        </Box>

        <Box
          p={{ base: 3, md: 5 }}
          borderRadius="12px"
          bg={theme.surface}
          border={`1px solid ${theme.border}`}
          boxShadow={theme.shadowSm}
        >
          {!selectedWarehouse ? (
            <Box py={{ base: 8, md: 14 }} textAlign="center">
              <Text fontWeight="700" color={theme.text}>
                Select a warehouse
              </Text>
              <Text mt={1} fontSize="sm" color={theme.textMuted}>
                The drivers already attached to that warehouse will appear here.
              </Text>
            </Box>
          ) : (
            <>
              <HStack justify="space-between" align="start" spacing={3} mb={4}>
                <Box minW={0}>
                  <Text
                    fontSize="lg"
                    fontWeight="800"
                    color={theme.text}
                    noOfLines={1}
                  >
                    {selectedWarehouse.name}
                  </Text>
                  <Text mt={0.5} fontSize="sm" color={theme.textMuted}>
                    {selectedWarehouse.address}
                  </Text>
                </Box>

                <Badge
                  colorScheme="blue"
                  borderRadius="6px"
                  px={2}
                  py={1}
                  flexShrink={0}
                >
                  {assignedDrivers.length}{" "}
                  {assignedDrivers.length === 1 ? "Driver" : "Drivers"}
                </Badge>
              </HStack>

              <HStack spacing={2} mb={4}>
                <Text fontSize="xs" color={theme.textMuted}>
                  {selectedWarehouse.lat}, {selectedWarehouse.lng}
                </Text>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() =>
                    loadWarehouseAssignments(
                      selectedWarehouse.id,
                      selectedOwnerUserId,
                    )
                  }
                  isLoading={loadingAssignments}
                >
                  Refresh
                </Button>
              </HStack>

              <Divider mb={4} />

              <HStack justify="space-between" mb={3}>
                <Text fontWeight="700" color={theme.text}>
                  Assigned Drivers
                </Text>
                {!loadingAssignments && (
                  <Text fontSize="xs" color={theme.textMuted}>
                    {assignedDrivers.length} assigned
                  </Text>
                )}
              </HStack>

              {loadingAssignments ? (
                <Box py={10} textAlign="center">
                  <Spinner />
                  <Text mt={2} fontSize="sm" color={theme.textMuted}>
                    Loading warehouse drivers…
                  </Text>
                </Box>
              ) : assignmentLoadError ? (
                <Box
                  p={4}
                  borderRadius="9px"
                  bg={theme.surfaceAlt}
                  border={`1px solid ${theme.border}`}
                >
                  <Text fontWeight="700" fontSize="sm" color={theme.text}>
                    Could not load assigned drivers
                  </Text>
                  <Text mt={1} fontSize="xs" color={theme.textMuted}>
                    {assignmentLoadError}
                  </Text>
                  <Button
                    mt={3}
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      loadWarehouseAssignments(
                        selectedWarehouse.id,
                        selectedOwnerUserId,
                      )
                    }
                  >
                    Try Again
                  </Button>
                </Box>
              ) : assignedDrivers.length === 0 ? (
                <Box
                  py={8}
                  px={4}
                  textAlign="center"
                  borderRadius="9px"
                  bg={theme.surfaceAlt}
                  border={`1px solid ${theme.border}`}
                >
                  <Text fontWeight="700" color={theme.text}>
                    No drivers assigned yet
                  </Text>
                  <Text mt={1} fontSize="sm" color={theme.textMuted}>
                    Select a driver and assign them to this warehouse.
                  </Text>
                </Box>
              ) : (
                <>
                  <VStack
                    display={{ base: "flex", md: "none" }}
                    align="stretch"
                    spacing={3}
                  >
                    {assignedDrivers.map((driver: any) => {
                      const availability = getDriverAvailability(driver);

                      return (
                        <Box
                          key={getDriverId(driver)}
                          p={3}
                          borderRadius="9px"
                          border={`1px solid ${theme.border}`}
                          bg={theme.surfaceAlt}
                        >
                          <HStack
                            justify="space-between"
                            align="start"
                            spacing={3}
                          >
                            <Box minW={0}>
                              <HStack spacing={2} flexWrap="wrap">
                                <Text
                                  fontWeight="800"
                                  color={theme.text}
                                  noOfLines={1}
                                >
                                  {driver.name || "Unnamed driver"}
                                </Text>
                                {driver.isPrimary && (
                                  <Badge
                                    colorScheme="blue"
                                    variant="subtle"
                                    borderRadius="6px"
                                  >
                                    Primary
                                  </Badge>
                                )}
                                {!driver.driverResolved && (
                                  <Badge
                                    colorScheme="orange"
                                    borderRadius="6px"
                                  >
                                    Profile missing
                                  </Badge>
                                )}
                              </HStack>

                              <Text fontSize="xs" color={theme.textMuted}>
                                {getDriverPhone(driver) || "No phone"}
                              </Text>
                            </Box>

                            <VStack spacing={1} align="flex-end" flexShrink={0}>
                              <Badge
                                colorScheme={
                                  availability.online ? "green" : "gray"
                                }
                                borderRadius="6px"
                              >
                                {availability.online ? "Online" : "Offline"}
                              </Badge>
                              <Badge
                                colorScheme={
                                  availability.eligible ? "green" : "orange"
                                }
                                borderRadius="6px"
                              >
                                {availability.eligible
                                  ? "Eligible"
                                  : "Not eligible"}
                              </Badge>
                            </VStack>
                          </HStack>

                          <SimpleGrid columns={2} spacing={3} mt={3}>
                            <Box>
                              <Text fontSize="xs" color={theme.textMuted}>
                                Vehicle
                              </Text>
                              <Text
                                fontSize="sm"
                                color={theme.text}
                                textTransform="capitalize"
                              >
                                {driver.vehicleType || "—"}
                              </Text>
                            </Box>
                            <Box>
                              <Text fontSize="xs" color={theme.textMuted}>
                                Jobs
                              </Text>
                              <Text fontSize="sm" color={theme.text}>
                                {availability.currentJobs} /{" "}
                                {availability.maxConcurrentJobs}
                              </Text>
                            </Box>
                          </SimpleGrid>

                          {!availability.eligible && driver.driverResolved && (
                            <Text mt={2} fontSize="xs" color={theme.textMuted}>
                              {availability.reason}
                            </Text>
                          )}
                        </Box>
                      );
                    })}
                  </VStack>

                  <Box
                    display={{ base: "none", md: "block" }}
                    overflowX="auto"
                    sx={{ WebkitOverflowScrolling: "touch" }}
                  >
                    <Table size="sm" minW="720px">
                      <Thead>
                        <Tr>
                          <Th>Driver</Th>
                          <Th>Vehicle</Th>
                          <Th>Online</Th>
                          <Th>Eligible</Th>
                          <Th>Jobs</Th>
                          <Th>Assignment</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {assignedDrivers.map((driver: any) => {
                          const availability = getDriverAvailability(driver);

                          return (
                            <Tr key={getDriverId(driver)}>
                              <Td>
                                <Text
                                  fontWeight="700"
                                  color={theme.text}
                                  whiteSpace="nowrap"
                                >
                                  {driver.name || "—"}
                                </Text>
                                <Text
                                  fontSize="xs"
                                  color={theme.textMuted}
                                  whiteSpace="nowrap"
                                >
                                  {getDriverPhone(driver) || "—"}
                                </Text>
                                {!driver.driverResolved && (
                                  <Badge
                                    mt={1}
                                    colorScheme="orange"
                                    borderRadius="6px"
                                  >
                                    Profile missing
                                  </Badge>
                                )}
                              </Td>
                              <Td textTransform="capitalize">
                                {driver.vehicleType || "—"}
                              </Td>
                              <Td>
                                <Badge
                                  colorScheme={
                                    availability.online ? "green" : "gray"
                                  }
                                  borderRadius="6px"
                                >
                                  {availability.online ? "Online" : "Offline"}
                                </Badge>
                              </Td>
                              <Td>
                                <VStack align="start" spacing={0.5}>
                                  <Badge
                                    colorScheme={
                                      availability.eligible ? "green" : "orange"
                                    }
                                    borderRadius="6px"
                                  >
                                    {availability.eligible
                                      ? "Eligible"
                                      : "Not eligible"}
                                  </Badge>
                                  {!availability.eligible &&
                                    driver.driverResolved && (
                                      <Text
                                        fontSize="10px"
                                        color={theme.textMuted}
                                        whiteSpace="nowrap"
                                      >
                                        {availability.reason}
                                      </Text>
                                    )}
                                </VStack>
                              </Td>
                              <Td whiteSpace="nowrap">
                                {availability.currentJobs} /{" "}
                                {availability.maxConcurrentJobs}
                              </Td>
                              <Td>
                                <Badge
                                  colorScheme={
                                    driver.isPrimary ? "blue" : "gray"
                                  }
                                  borderRadius="6px"
                                  variant="subtle"
                                >
                                  {driver.isPrimary
                                    ? "Primary"
                                    : driver.isBackup
                                      ? "Backup"
                                      : "Assigned"}
                                </Badge>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </SimpleGrid>

      <Box
        mt={5}
        p={{ base: 3, md: 5 }}
        borderRadius="12px"
        bg={theme.surface}
        border={`1px solid ${theme.border}`}
        boxShadow={theme.shadowSm}
      >
        <HStack
          justify="space-between"
          align="start"
          flexWrap="wrap"
          spacing={3}
          mb={4}
        >
          <Box>
            <Text fontWeight="800" fontSize="lg" color={theme.text}>
              Driver Pool
            </Text>
            <Text fontSize="sm" color={theme.textMuted}>
              All drivers and their current dispatch status.
            </Text>
          </Box>

          <HStack spacing={2}>
            <Badge colorScheme="green" borderRadius="6px">
              {driverStats.online} Online
            </Badge>
            <Badge colorScheme="green" variant="outline" borderRadius="6px">
              {driverStats.eligible} Eligible
            </Badge>
          </HStack>
        </HStack>

        {loading ? (
          <Box py={8} textAlign="center">
            <Spinner />
          </Box>
        ) : drivers.length === 0 ? (
          <Box py={8} textAlign="center">
            <Text color={theme.textMuted}>No drivers found.</Text>
          </Box>
        ) : (
          <>
            <VStack
              display={{ base: "flex", md: "none" }}
              align="stretch"
              spacing={3}
            >
              {drivers.map((driver) => {
                const id = getDriverId(driver);
                const availability = getDriverAvailability(driver);

                return (
                  <Box
                    key={id || driver.name}
                    p={3}
                    borderRadius="9px"
                    bg={theme.surfaceAlt}
                    border={`1px solid ${theme.border}`}
                  >
                    <HStack justify="space-between" align="start" spacing={3}>
                      <Box minW={0}>
                        <Text fontWeight="800" color={theme.text} noOfLines={1}>
                          {driver.name || "Unnamed driver"}
                        </Text>
                        <Text fontSize="xs" color={theme.textMuted}>
                          {getDriverPhone(driver) || "No phone"}
                        </Text>
                        <Text
                          mt={1}
                          fontSize="xs"
                          color={theme.textMuted}
                          textTransform="capitalize"
                        >
                          {driver.vehicleType || "—"} •{" "}
                          {availability.currentJobs}/
                          {availability.maxConcurrentJobs} jobs
                        </Text>
                      </Box>

                      <VStack spacing={1} align="flex-end" flexShrink={0}>
                        <Badge
                          colorScheme={availability.online ? "green" : "gray"}
                          borderRadius="6px"
                        >
                          {availability.online ? "Online" : "Offline"}
                        </Badge>
                        <Badge
                          colorScheme={
                            availability.eligible ? "green" : "orange"
                          }
                          borderRadius="6px"
                        >
                          {availability.eligible ? "Eligible" : "Not eligible"}
                        </Badge>
                      </VStack>
                    </HStack>
                  </Box>
                );
              })}
            </VStack>

            <Box
              display={{ base: "none", md: "block" }}
              overflowX="auto"
              sx={{ WebkitOverflowScrolling: "touch" }}
            >
              <Table size="sm" minW="900px">
                <Thead>
                  <Tr>
                    <Th>Driver</Th>
                    <Th>Cell</Th>
                    <Th>WhatsApp</Th>
                    <Th>Vehicle</Th>
                    <Th>Online</Th>
                    <Th>Eligible</Th>
                    <Th>Shift</Th>
                    <Th>Jobs</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {drivers.map((driver) => {
                    const id = getDriverId(driver);
                    const availability = getDriverAvailability(driver);

                    return (
                      <Tr key={id || driver.name}>
                        <Td>
                          <Text
                            fontWeight="700"
                            color={theme.text}
                            whiteSpace="nowrap"
                          >
                            {driver.name || "—"}
                          </Text>
                        </Td>
                        <Td whiteSpace="nowrap">{driver.phoneNumber || "—"}</Td>
                        <Td whiteSpace="nowrap">
                          {driver.whatsappNumber || "—"}
                        </Td>
                        <Td textTransform="capitalize">
                          {driver.vehicleType || "—"}
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={availability.online ? "green" : "gray"}
                            borderRadius="6px"
                          >
                            {availability.online ? "Online" : "Offline"}
                          </Badge>
                        </Td>
                        <Td>
                          <VStack spacing={0.5} align="start">
                            <Badge
                              colorScheme={
                                availability.eligible ? "green" : "orange"
                              }
                              borderRadius="6px"
                            >
                              {availability.eligible
                                ? "Eligible"
                                : "Not eligible"}
                            </Badge>
                            {!availability.eligible && (
                              <Text
                                fontSize="10px"
                                color={theme.textMuted}
                                whiteSpace="nowrap"
                              >
                                {availability.reason}
                              </Text>
                            )}
                          </VStack>
                        </Td>
                        <Td textTransform="capitalize" whiteSpace="nowrap">
                          {String(driver.currentShiftStatus || "—").replace(
                            /_/g,
                            " ",
                          )}
                        </Td>
                        <Td whiteSpace="nowrap">
                          {availability.currentJobs} /{" "}
                          {availability.maxConcurrentJobs}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default WarehouseAssignmentsPage;
